import { Injectable } from '@angular/core';
import { Logger, ServerRootService } from '@se/se.ui.html5';
import { BusinessUnit, Examination, GeneralObject, JsonDataStructure, LoadedDataFromRoute, OriginPage, UploadInfo } from '../models';
import { TranslateService } from '@ngx-translate/core';
import { DatePipe } from '@angular/common';
import { FormGroup } from '@angular/forms';
import { Guid } from '@lib/tools/guid';
import { SendingApplicationService } from '../sending-application.service';
import { UploadExternalData } from '@se/platform';

@Injectable({
    providedIn: 'root',
})

export class HelperService extends ServerRootService {

    private isses: string = '';
    private eventInfo: GeneralObject = null;
    private examinationSection: Array<Examination> = [];
    private optionsBusinessUnit: Array<BusinessUnit> = [];
    private originPage: OriginPage = null;
    private COMPLETE_FILE_EXTENSIONS: Array<string> = ['.png', '.jpg', '.jpeg', '.pdf', '.p7m', '.pades', '.txt', '.doc', '.bmp', '.PNG', '.JPG', '.JPEG', '.PDF', '.P7M', '.PADES', '.TXT', '.DOC', '.BMP'];
    private UNCOMPLETE_FILE_EXTENSIONS: Array<string> = ['.png', '.jpg', '.jpeg', '.bmp', '.PNG', '.JPG', '.JPEG', '.BMP'];


    constructor(
        private logger: Logger,
        private sendingApplicationService: SendingApplicationService,
        private translate: TranslateService,
        public datePipe: DatePipe,
    ) {
        super();
    }

    createBody(file: File, fileIndex: number, formData: FormGroup, loadedData: LoadedDataFromRoute): JsonDataStructure {
        
        this.examinationSection = this.settingExaminationSection(formData, loadedData);

        return {
            EventInfo: {
                ExternalApplication: this.eventInfo
            },
            Patient: {
                Surname: this.extractFormGroup('patientData', formData).controls.surname.value,
                Name: this.extractFormGroup('patientData', formData).controls.name.value,
                Sex: this.extractFormGroup('patientData', formData).controls.sex.value,
                TaxCode: this.extractFormGroup('patientData', formData).controls.taxCode.value,
                BirthDate: this.transformDateNoTime(this.extractFormGroup('patientData', formData).controls.birthDate.value),
                Keys: [
                    {
                        PatientId: this.extractFormGroup('patientData', formData).controls.patientId.value,
                        Issuer: this.extractFormGroup('patientData', formData).controls.issuer.value,
                        IsExtPatient: true, //CONST
                    },
                ]
            },
            Examinations: this.examinationSection,
            CreatorPhysician: {
                FamilyName: this.extractFormGroup('physicianData', formData).controls.referringPhysicianSurname.value,
                GivenName: this.extractFormGroup('physicianData', formData).controls.referringPhysicianName.value,
            },
            DocumentApproved: {
                Number: 'VNA@' + this.transformDateAndTime(new Date()) + '@' + fileIndex,
                Format: this.extractDocumentType(file),
                Encoding: "Base64", //CONST
                Date: this.transformDateAndTime(new Date()),
            },
            Store: true, //CONST
        }
    }

    transformDateAndTime(date: string | number | Date) {
        return this.datePipe.transform(date, 'yyyy-MM-dd HH:mm:ss', null, this.translate.currentLang)?.toString();
    }

    transformDateNoTime(date: string | number | Date) {
        return this.datePipe.transform(date, 'yyyy-MM-dd', null, this.translate.currentLang)?.toString();

    }

    private extractFormGroup(nestedFormGroup: string, formData: FormGroup): any {
        return formData.controls[nestedFormGroup];
    }

    private extractDocumentType(document: File): string {
        return document.type.split('/')[1];
    }

    configurationDataEstensa(vnaUploadExternalData: UploadExternalData): LoadedDataFromRoute {
        this.originPage = this.extractOriginPage(vnaUploadExternalData.from);

        this.setEventInfoSection();

        switch (this.originPage) {
            case (OriginPage.VNA_PATIENT_FOLDER):
                return this.transformDataVna(vnaUploadExternalData);
            case (OriginPage.ESTENSARADIO):
                return this.transformDataEstensa(vnaUploadExternalData);
            case (OriginPage.TELECONSULTATION):
                return this.transformDataEstensa(vnaUploadExternalData);
            case (OriginPage.EXECUTION_SEARCH):
                return this.transformDataEstensa(vnaUploadExternalData);
            case (OriginPage.ESTENSACARDIO):
                return this.transformDataCardio(vnaUploadExternalData);
            default:
                this.logger.error('no origin retrieved');
        }
    }

    private extractOriginPage(originSelected: string): OriginPage {
        return originSelected as OriginPage;
    }

    configurationDataFromThirdPartiesNotDimcom(info: UploadInfo): LoadedDataFromRoute {
        this.originPage = OriginPage.EXTERNAL_NOT_DICOM;
        this.issesThirdParties(info);
        return this.trasformDataExternalNotDicom(info);
    }

    private trasformDataExternalNotDicom(infoThirdParties: UploadInfo): LoadedDataFromRoute {
        this.originPage = OriginPage.EXTERNAL_NOT_DICOM;
        return {
            uploadInfo: {
                Name: infoThirdParties.Name,
                Surname: infoThirdParties.Surname,
                PatientId: infoThirdParties.PatientId,
                BirthDate: infoThirdParties.BirthDate,
                TaxCode: infoThirdParties.TaxCode,
                Sex: infoThirdParties.Sex,
                OrderProvenance: infoThirdParties.OrderProvenance,
                Issuer: infoThirdParties.Issuer,
                OrderDescription: infoThirdParties.OrderDescription,
                OrderDate: infoThirdParties.OrderDate, // new Date().toJSON(),
                OrderNumber: infoThirdParties.OrderNumber, //'',
                OrderBusinessUnit: null,
                SendingApplication: infoThirdParties.SendingApplication,
                ReferringPhysicianName: infoThirdParties.ReferringPhysicianName,
                ReferringPhysicianSurname: infoThirdParties.ReferringPhysicianSurname
            },
            acceptedFormats: this.COMPLETE_FILE_EXTENSIONS
        }
    }

    private transformDataVna(vnaUploadExternalData: UploadExternalData): LoadedDataFromRoute {
        return {
            uploadInfo: {
                Name: vnaUploadExternalData.selectedPatient?.firstName,
                Surname: vnaUploadExternalData.selectedPatient?.lastName,
                PatientId: vnaUploadExternalData.selectedPatient?.identifier,
                BirthDate: vnaUploadExternalData.selectedPatient?.birthDate,
                TaxCode: vnaUploadExternalData.selectedPatient?.taxCode,
                Sex: vnaUploadExternalData.selectedPatient?.sex.code,
                OrderProvenance: '',
                Issuer: vnaUploadExternalData.selectedPatient?.issuer,
                OrderBusinessUnit: null,
                OrderDescription: '',
                OrderDate: new Date().toJSON(),
                OrderNumber: '',
                ReferringPhysicianName: vnaUploadExternalData.loggedUser.name,
                ReferringPhysicianSurname: vnaUploadExternalData.loggedUser.surname
            },
            acceptedFormats: this.COMPLETE_FILE_EXTENSIONS
        }
    }

    private transformDataEstensa(vnaUploadExternalData: UploadExternalData): LoadedDataFromRoute {
        return {
            uploadInfo: {
                Name: vnaUploadExternalData.selectedExamination.patient.firstName,
                Surname: vnaUploadExternalData.selectedExamination.patient.lastName,
                PatientId: vnaUploadExternalData.selectedExamination.patient.identifier,
                BirthDate: vnaUploadExternalData.selectedExamination.patient.birthDate,
                TaxCode: vnaUploadExternalData.selectedExamination.patient.taxCode,
                Sex: vnaUploadExternalData.selectedExamination.patient.sex.code,
                OrderProvenance: vnaUploadExternalData.selectedExamination.provenance.description,
                Issuer: vnaUploadExternalData.selectedExamination.patient.issuer,
                OrderBusinessUnit: {
                    id: vnaUploadExternalData.selectedExamination.businessUnit.id,
                    code: vnaUploadExternalData.selectedExamination.businessUnit.code,
                    description: vnaUploadExternalData.selectedExamination.businessUnit.description
                },
                OrderDescription: vnaUploadExternalData.selectedExamination.exam.description,
                OrderDate: vnaUploadExternalData.selectedExamination.admissionDate,
                OrderNumber: vnaUploadExternalData.selectedExamination.accessionNumber,
                ReferringPhysicianName: vnaUploadExternalData.loggedUser.name,
                ReferringPhysicianSurname: vnaUploadExternalData.loggedUser.surname
            },
            acceptedFormats: this.UNCOMPLETE_FILE_EXTENSIONS,
            estensaField: {
                SUID: vnaUploadExternalData.selectedExamination.studyInstanceUID,
                Isses: this.isses,
                StepId: vnaUploadExternalData.selectedExamination.id
            }
        }
    }

    private transformDataCardio(data: any): LoadedDataFromRoute {
        return {
            uploadInfo: {
                Name: data.selectedExamination.Patient.FirstName,
                Surname: data.selectedExamination.Patient.LastName,
                PatientId: data.selectedExamination.Patient.Id,
                BirthDate: data.selectedExamination.Patient.BirthDate,
                TaxCode: data.selectedExamination.Patient.TaxCode,
                Sex: data.selectedExamination.Patient.SexCode,
                OrderProvenance: data.selectedExamination.ReferenceExamination.IdProvenance.Description,
                Issuer: data.selectedExamination.Patient.Issuer,
                OrderBusinessUnit: {
                    id: data.selectedExamination.ReferenceExamination.IdBusinessUnit.Id,
                    code: data.selectedExamination.ReferenceExamination.IdBusinessUnit.Code,
                    description: data.selectedExamination.ReferenceExamination.IdBusinessUnit.Description
                },
                OrderDescription: data.selectedExamination.ReferenceExamination.IdExam.Description,
                OrderDate: data.selectedExamination.ReferenceExamination.AdmissionDate,
                OrderNumber: data.selectedExamination.ReferenceExamination.AdmissionNumber,
                ReferringPhysicianName: data.selectedExamination.ReferenceExamination.IdReferringPhysician.Name,
                ReferringPhysicianSurname: data.selectedExamination.ReferenceExamination.IdReferringPhysician.Surname
            },
            acceptedFormats: this.UNCOMPLETE_FILE_EXTENSIONS,
            estensaField: {
                //Modality: data.exam.IdExam.Description ? data.exam.IdExam.Description : 'OT',
                SUID: data.selectedExamination.ReferenceExamination.StudyInstanceUid,
                Isses: this.isses,
                StepId: data.selectedExamination.ReferenceExamination.IdExam.Id
            }
        }
    }

    //**isses for eventInfo section
    private setEventInfoSection() {
        this.sendingApplicationService.getIsses('3').subscribe({
            next: (info: Array<string>) => {
                if (info) {
                    this.eventInfo = this.retriveEventInfoObjectEstensa(info[0]);
                }
            },
            error: (error) => {
                this.logger.error('Errore caricamento isses da sending application');
                this.eventInfo = { Description: '' };
            }
        });
    }

    private retriveEventInfoObjectEstensa(isses: string): GeneralObject {
        return {
            Description: isses
        }
    }

    private issesThirdParties(uploadInfo: UploadInfo) {
        this.eventInfo = {
            Description: uploadInfo.SendingApplication
        }
    }
    //**end isses for eventInfo section


    //**examination Section

    private settingExaminationSection(formData: FormGroup, loadedData: LoadedDataFromRoute): Examination[] {
        switch (this.originPage) {
            case (OriginPage.EXTERNAL_NOT_DICOM):
                return this.examinationSectionThirdParties(formData);
            case (OriginPage.VNA_PATIENT_FOLDER):
                return this.examinationSectionVna(formData);
            case (OriginPage.ESTENSARADIO):
                return this.examinationSectionEstensa(formData, loadedData);
            case (OriginPage.ESTENSACARDIO):
                return this.examinationSectionEstensa(formData, loadedData);
            case (OriginPage.TELECONSULTATION):
                return this.examinationSectionEstensa(formData, loadedData);
            case (OriginPage.EXECUTION_SEARCH):
                return this.examinationSectionEstensa(formData, loadedData);
            default:
                this.logger.error('no origin retrieved');
        }
    }

    private examinationSectionVna(formData: FormGroup): Examination[] {
        return [
            {
                Type: "NW-SC", //CONST
                StepID: new Guid().toString(),
                ExamEventType: {
                    Description: "IN_NEWREPORT", //CONST
                },
                ExamDate: this.transformDateAndTime(this.extractFormGroup('orderData', formData).controls.orderDate.value),
                AccessionNumber: this.extractFormGroup('orderData', formData).controls.orderNumber.value,
                Exam: {
                    Description: this.extractFormGroup('orderData', formData).controls.orderDescription.value,
                    //Modality: 'OT', 
                },
                Provenance: {
                    Description: this.extractFormGroup('orderData', formData).controls.orderProvenance.value,
                },
                BusinessUnit: {
                    id: this.extractFormGroup('orderData', formData).controls.orderBusinessUnit.value.id,
                    description: this.extractFormGroup('orderData', formData).controls.orderBusinessUnit.value.description,
                    code: this.extractFormGroup('orderData', formData).controls.orderBusinessUnit.value.code,

                }
            }
        ]
    }

    private examinationSectionThirdParties(formData: FormGroup): Examination[] {
        return [
            {
                Type: "NW-SC", //CONST
                StepID: new Guid().toString(),
                ExamEventType: {
                    Description: "IN_NEWREPORT", //CONST
                },
                ExamDate: this.transformDateAndTime(this.extractFormGroup('orderData', formData).controls.orderDate.value),
                AccessionNumber: this.extractFormGroup('orderData', formData).controls.orderNumber.value,
                Exam: {
                    Description: this.extractFormGroup('orderData', formData).controls.orderDescription.value,
                    //Modality: 'OT',
                },
                Provenance: {
                    Description: this.extractFormGroup('orderData', formData).controls.orderProvenance.value,
                }
            }
        ]
    }

    private examinationSectionEstensa(formData: FormGroup, loadedData: LoadedDataFromRoute): Examination[] {
        return [
            {
                Type: "NW-SC", //CONST
                StepID: loadedData.estensaField?.StepId ? loadedData.estensaField?.StepId : '',
                ExamEventType: {
                    Description: "IN_NEWREPORT", //CONST
                },
                ExamDate: this.transformDateAndTime(this.extractFormGroup('orderData', formData).controls.orderDate.value),
                AccessionNumber: this.extractFormGroup('orderData', formData).controls.orderNumber.value,
                SUID: loadedData.estensaField?.SUID ? loadedData.estensaField.SUID : '',
                Exam: {
                    Description: this.extractFormGroup('orderData', formData).controls.orderDescription.value,
                    //Modality: loadedData.estensaField.Modality ? loadedData.estensaField.Modality : 'OT',
                },
                Provenance: {
                    Description: this.extractFormGroup('orderData', formData).controls.orderProvenance.value,
                },
                BusinessUnit: {
                    id: loadedData.uploadInfo.OrderBusinessUnit?.id ? loadedData.uploadInfo.OrderBusinessUnit.id : this.extractFormGroup('orderData', formData).controls.orderBusinessUnit.value.id,
                    description: loadedData.uploadInfo.OrderBusinessUnit?.description ? loadedData.uploadInfo.OrderBusinessUnit.description : this.extractFormGroup('orderData', formData).controls.orderBusinessUnit.value.description,
                    code: loadedData.uploadInfo.OrderBusinessUnit?.code ? loadedData.uploadInfo.OrderBusinessUnit.code : this.extractFormGroup('orderData', formData).controls.orderBusinessUnit.value.code,
                }
            }
        ]
    }
    //**end examination section
}
